import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthFetch } from './useAuthFetch';
import {
  buildProjectSearchIndex,
  fetchProjectList,
  filterProjectSearchIndex,
  MIN_PWA_GLOBAL_SEARCH_LENGTH,
  readCachedProjectList,
} from '../utils/pwaGlobalSearch';

const SEARCH_DEBOUNCE_MS = 150;

export function usePwaGlobalSearch(isOpen) {
  const { authFetch, isReady } = useAuthFetch();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [projects, setProjects] = useState(() => readCachedProjectList() || []);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const refreshRequestRef = useRef(0);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setDebouncedQuery(query);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [isOpen, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setDebouncedQuery('');
      setLoadError('');
      return;
    }

    const cached = readCachedProjectList();
    if (cached) {
      setProjects(cached);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isReady) {
      return undefined;
    }

    let cancelled = false;
    const requestId = refreshRequestRef.current + 1;
    refreshRequestRef.current = requestId;

    const refreshProjects = async () => {
      const hasCachedProjects = Boolean(readCachedProjectList()?.length);
      if (!hasCachedProjects) {
        setIsLoading(true);
      }
      setLoadError('');

      try {
        const nextProjects = await fetchProjectList(authFetch);
        if (!cancelled && refreshRequestRef.current === requestId) {
          setProjects(nextProjects);
        }
      } catch (error) {
        if (!cancelled && refreshRequestRef.current === requestId) {
          setLoadError(error.message || 'Unable to load projects');
        }
      } finally {
        if (!cancelled && refreshRequestRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    refreshProjects();

    return () => {
      cancelled = true;
    };
  }, [authFetch, isOpen, isReady]);

  const searchIndex = useMemo(() => buildProjectSearchIndex(projects), [projects]);

  const results = useMemo(
    () => filterProjectSearchIndex(searchIndex, debouncedQuery),
    [debouncedQuery, searchIndex]
  );

  const clearQuery = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  const showMinCharsHint = query.trim().length > 0
    && query.trim().length < MIN_PWA_GLOBAL_SEARCH_LENGTH;

  const showNoResults = debouncedQuery.trim().length >= MIN_PWA_GLOBAL_SEARCH_LENGTH
    && results.length === 0
    && !isLoading;

  return {
    query,
    setQuery,
    clearQuery,
    results,
    isLoading,
    loadError,
    showMinCharsHint,
    showNoResults,
    hasProjects: projects.length > 0,
    minSearchLength: MIN_PWA_GLOBAL_SEARCH_LENGTH,
  };
}
